import {useEffect, useRef, useState} from 'react';
import {useStore} from 'react-redux';
import useResizeObserver from 'use-resize-observer';
import {initKpParent} from '../../../../app/initKpParent.function';
import {AppLinkFallbackType} from '../../../../applink/AppLinkFallbackType.enum';
import {pagewebUrlMatcher} from '../../../../applink/PagewebUrlMatcher.singleton';
import {requestContext} from '../../../../common/requestContext';
import {IS_CLIENT_SIDE} from '../../../../common/shared/utils/constants';
import {AppDownload} from '../../../../design-system/organisms/AppDownload/AppDownload';
import {LOG_CLICK_EVENT} from '../../../../logging/logEvent';
import {putTiaraObjectProps} from '../../../../logging/putTiaraObjectProps';
import {AppStore} from '../../../../state/store';
import {MESSAGE_UNSUPPORTED_BROWSER, V3_EVENT_IFRAME_ID} from '../constant';
import {EventPageV3ContainerProps} from '../type';

export function EventPageV3Container({
  hashedEventId,
  eventTitle,
}: EventPageV3ContainerProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const {isMobile, isWebView} = requestContext.requestInfo.display;
  const store = useStore();

  const {ref: eventPageBodyRef} = useResizeObserver<HTMLElement>({
    onResize: ({height}) => {
      if (!height) {
        return;
      }
      const iframe = iframeRef.current;
      if (iframe && iframe.contentWindow) {
        if (iframe.contentWindow) {
          iframe.style.height = `${height}px`;
        }
      }
    },
  });

  useEffect(() => {
    if (iframeRef.current) {
      IS_CLIENT_SIDE && initKpParent(store as AppStore);
      // iframe 내부 컨텐츠가 캐싱되어 있는 경우에, 리액트가 load 이벤트 핸들러를 iframe에 attach하기 전에
      // load 이벤트가 발생해 load 이벤트 핸들러가 호출되지 않을 수 있습니다.
      // 이를 위해 useEffect에서 src에 iframe 내에서 로딩할 링크를 넣어줌으로써,
      // 리액트가 반드시 iframe에 핸들러를 attach 한 이후에 iframe 내부 컨텐츠를 로딩하도록 강제합니다.
      // 참고: https://github.com/facebook/react/issues/6541#issuecomment-1174249634
      iframeRef.current.src = `/event/${hashedEventId}/internal${window.location.search}`;
    }
  }, [hashedEventId, store]);

  function onLoadIframe() {
    const iframe = iframeRef.current;
    if (iframe && iframe.contentWindow) {
      // iframe 내 window에 KPWeb을 주입합니다.
      iframe.contentWindow.KPWeb = window.KPWeb;
      iframe.style.height = `${iframe.contentWindow.document.documentElement.scrollHeight}px`;
      eventPageBodyRef(iframe.contentWindow.document.documentElement);
    }
  }

  const [appDownloadLink, setAppDownloadLink] = useState('');

  useEffect(() => {
    // getCurrentAppLink 내부에서 window를 사용하므로, 실제로 페이지가 렌더링 된 후에
    // appDownloadLink 값을 계산할 수 있습니다. 이를 위해 state로 관리합니다.
    setAppDownloadLink(
      pagewebUrlMatcher.getCurrentAppLink({
        schemePrefixType: 'applink',
        fallbackType: AppLinkFallbackType.Store,
      })
    );
  }, []);

  return (
    <>
      <div className="h-full! flex-center-full-grow">
        <iframe
          id={V3_EVENT_IFRAME_ID}
          ref={iframeRef}
          // deprecated 되었다고 하나 실제 동작하는 브라우저가 존재해서, 자연스러운 UI를 위해 추가했습니다.
          scrolling="no"
          className="m-0 h-full w-full max-w-720pxr overflow-hidden p-0"
          onLoad={onLoadIframe}
        >
          {MESSAGE_UNSUPPORTED_BROWSER}
        </iframe>
      </div>
      {!isWebView && (
        <div className={isMobile ? 'mt-24pxr px-15pxr' : 'mt-48pxr'}>
          <AppDownload
            isMobile={isMobile}
            onClickAppLink={e =>
              LOG_CLICK_EVENT('앱설치배너_클릭', e, () =>
                window.location.assign(appDownloadLink)
              )
            }
            {...putTiaraObjectProps({
              page_meta: {
                id: hashedEventId,
                type: 'eventId',
                name: eventTitle,
              },
              eventMeta: {
                id: appDownloadLink,
                type: 'scheme',
              },
              click: {
                layer1: '앱설치배너',
              },
            })}
          />
        </div>
      )}
    </>
  );
}
